import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { SiteHeader } from '../../components/SiteHeader';
import { SiteFooter } from '../../components/SiteFooter';
import { Card } from '../../components/ui/Card';
import { Reveal } from '../../components/landing/Reveal';

interface Role {
  id: string;
  team: string;
  location: string;
}

const ROLES: Role[] = [
  { id: 'founding-eng-tokyo', team: 'Engineering', location: 'Tokyo or remote (JP timezone)' },
  { id: 'founding-eng-eu',    team: 'Engineering', location: 'Berlin or remote (CET ± 2h)' },
  { id: 'founding-cs-jp',     team: 'Customer Success', location: 'Tokyo on-site' },
  { id: 'founding-design',    team: 'Design',     location: 'Remote (JP / EU)' },
];

export function CareersPage() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />
      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-12 lg:py-16">
        <Reveal as="div">
          <span className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]">
            <span className="sparkle-dot" aria-hidden />
            {t('careers.eyebrow')}
          </span>
          <h1 className="display text-4xl lg:text-5xl font-semibold tracking-tight mt-4">
            {t('careers.title')}
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl leading-relaxed">
            {t('careers.subtitle')}
          </p>
        </Reveal>

        <div className="rule mt-10" aria-hidden />

        <Reveal as="div" className="mt-12 grid md:grid-cols-3 gap-4">
          <ValueCard k="why" />
          <ValueCard k="how" />
          <ValueCard k="who" />
        </Reveal>

        <Reveal as="div" className="mt-14">
          <h2 className="display text-2xl font-semibold mb-5">{t('careers.openRoles')}</h2>
          {ROLES.length === 0 ? (
            <Card className="rounded-2xl border-border p-10 text-center text-muted-foreground">
              {t('careers.noOpenRoles')}
            </Card>
          ) : (
            <ul className="divide-y divide-border border border-border rounded-2xl bg-card overflow-hidden">
              {ROLES.map((r) => (
                <li
                  key={r.id}
                  className="px-5 py-4 flex items-center justify-between gap-4 transition-colors hover:bg-muted/50"
                >
                  <div className="min-w-0">
                    <div className="font-medium">{t(`careers.roles.${r.id}.title`)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {r.team} · {r.location}
                    </div>
                  </div>
                  <a
                    href={`mailto:careers@plyez.com?subject=Application: ${encodeURIComponent(t(`careers.roles.${r.id}.title`))}`}
                    className="inline-flex items-center gap-1 text-sm text-accent-700 dark:text-accent-300 font-medium underline underline-offset-4 shrink-0 rounded-sm"
                  >
                    {t('careers.apply')}
                    <ArrowUpRight size={14} className="rtl-flip" aria-hidden />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </Reveal>

        <Reveal as="div">
          <Card className="relative overflow-hidden mt-10 rounded-2xl p-6 lg:p-8 bg-foreground text-background border-foreground">
            <div className="text-[11px] uppercase tracking-[0.18em] opacity-70 mb-2">
              {t('careers.noFitEyebrow')}
            </div>
            <div className="display text-xl font-semibold">{t('careers.noFitTitle')}</div>
            <p className="mt-2 text-sm opacity-90 max-w-2xl leading-relaxed">{t('careers.noFitBody')}</p>
            <Link
              to="/contact"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium underline underline-offset-4 rounded-sm"
            >
              {t('careers.noFitCta')}
              <ArrowUpRight size={14} className="rtl-flip" aria-hidden />
            </Link>
          </Card>
        </Reveal>
      </main>
      <SiteFooter />
    </div>
  );
}

function ValueCard({ k }: { k: string }) {
  const { t } = useTranslation();
  return (
    <Card className="rounded-2xl border-border p-5 h-full">
      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        {t(`careers.values.${k}.label`)}
      </div>
      <div className="display text-lg font-semibold mt-2">{t(`careers.values.${k}.title`)}</div>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
        {t(`careers.values.${k}.body`)}
      </p>
    </Card>
  );
}
